import React, { useState, ChangeEvent } from "react";
import { headerStyle, sectionStyle } from "./formConstants";
import { useSharedValue } from "../components/Constants/constants";
import "../Forms/style.css";
import {
  FormCheckBox,
  FormDate,
  FormInput,
  FormsubTitle,
  FormTitle,
  createRequiredLabel,
} from "./common";
import CountryDropdown from "./CountryDropdown";
import { W8IMYFormModel } from "../Models/W8IMYModels";
import { useW8IMYForm } from "../Hooks/useW8IMYForm";
import { W8IMYService } from "../services/W8IMYService";
import { validateTINFormat } from "../utils/formUtils";

interface W8IMYFormProps {
  selectedRowData?: any;
  row?: any;
  selectedIndex?: number;
  onSaveSuccess?: (row: any) => void;
}

function W8IMYForm({ selectedRowData, row, selectedIndex = 0, onSaveSuccess }: W8IMYFormProps) {
  const { isMenuOpen } = useSharedValue();

  // Use W8IMY form hook for state management
  const {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  } = useW8IMYForm({
    initialData: selectedRowData,
  });

  // Legacy form data state for UI compatibility
  const [formData, setFormData] = useState(() => {
    // Convert model to form data format for legacy UI
    const modelData = model as any;
    return {
      ...modelData,
    };
  });

  // Update formData whenever model changes
  React.useEffect(() => {
    setFormData({ ...model });
  }, [model]);

  // Watch for changes in selectedRowData and update form fields accordingly
  React.useEffect(() => {
    if (selectedRowData) {
      // 🛡️ CRITICAL PROTECTION: Don't override user selections if form is already mapped
      if (model?.isMapDone || selectedRowData?.isMapDone) {
        // console.log(
        //   "🛡️ BLOCKED W8IMY selectedRowData updates - form already mapped, preserving user selections"
        // );
        return;
      }

      // Check if UniqueIdentifier has changed and update it
      const newUniqueIdentifier =
        selectedRowData.uniqueidentifier || selectedRowData.UniqueIdentifier;
      const currentUniqueIdentifier = formData.UniqueIdentifier;

      if (newUniqueIdentifier && newUniqueIdentifier !== currentUniqueIdentifier) {
        updateField("UniqueIdentifier", newUniqueIdentifier);
      }

      // Update other fields that might have changed
      Object.keys(selectedRowData).forEach((key) => {
        const value = selectedRowData[key];
        const currentValue = (formData as any)[key];

        // Only update if the value has actually changed and is not empty
        if (value && value !== currentValue) {
          // Handle checkbox fields: convert "selected" to boolean true, empty to boolean false
          if (typeof value === "string" && (value === "selected" || value === "")) {
            const booleanValue = value === "selected";
            // Only update if the boolean value is different from current
            if (booleanValue !== currentValue) {
              updateField(key, booleanValue);
            }
          }
          // Handle regular string fields
          else if (typeof value === "string") {
            updateField(key, value);
          }
          // Handle boolean fields directly
          else if (typeof value === "boolean") {
            updateField(key, value);
          }
        }
      });
    }
  }, [selectedRowData, updateField]);

  // Auto-check GIIN and Foreign TIN checkboxes when their corresponding fields have values
  React.useEffect(() => {
    // Auto-check GIIN checkbox when GIIN field has a value (IMY uses IMY-9A_giin field)
    const giinValue = formData["IMY-9A_giin"];
    const giinCheckBox = formData["GIINCheckBox"];
    if (giinValue && typeof giinValue === "string" && giinValue.trim() && !giinCheckBox) {
      updateField("GIINCheckBox", true);
    }

    // Auto-check Foreign TIN checkbox when IMY-9B_foreign_tax_identifying_number has a value
    const foreignTinValue = formData["IMY-9B_foreign_tax_identifying_number"];
    const foreignTinCheckBox = formData["Foreign TIN"];
    if (
      foreignTinValue &&
      typeof foreignTinValue === "string" &&
      foreignTinValue.trim() &&
      !foreignTinCheckBox
    ) {
      updateField("Foreign TIN", true);
    }
  }, [
    formData["IMY-9A_giin"],
    formData["IMY-9B_foreign_tax_identifying_number"],
    formData["GIINCheckBox"],
    formData["Foreign TIN"],
    updateField,
  ]);

  // Auto-detect and set Taxpayer type based on TIN value from API
  React.useEffect(() => {
    const tinValue = formData["IMY-8_us_tin"];
    const currentTaxpayerType = formData["Taxpayer type"];

    if (tinValue && tinValue.trim() !== "") {
      const trimmedTin = tinValue.trim();

      // Validate TIN format and get detected type (handles special IMY types: QIEIN, WPEIN, WTEIN, GIIN)
      const validation = validateTINFormat(trimmedTin, currentTaxpayerType);

      // Set validation message (warnings or success)
      setTinValidationMessage(validation.message);

      // If type was detected and it's different from current, update it
      if (validation.detectedType && validation.detectedType !== currentTaxpayerType) {
        updateField("Taxpayer type", validation.detectedType);

        // If GIIN detected, auto-check GIIN checkbox
        if (validation.detectedType === "GIIN" && !model?.isMapDone && !formData?.isMapDone) {
          updateField("GIINCheckBox", true);
        } else if (validation.detectedType === "GIIN") {
          console.log("🛡️ BLOCKED W8IMY GIIN auto-check from TIN detection - form already mapped");
        }
      }
      // If no type was detected, show warning but don't update type
      else if (!validation.detectedType && validation.message) {
        // console.log("⚠️ TIN from API could not be auto-detected:", {
        //   tin: trimmedTin,
        //   message: validation.message,
        // });
      }
    } else {
      // Clear validation message when TIN is empty
      setTinValidationMessage(null);
    }
  }, [formData["IMY-8_us_tin"], formData["Taxpayer type"], updateField]);

  // State to control validation summary display
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // State for TIN validation message
  const [tinValidationMessage, setTinValidationMessage] = useState<string | null>(null);

  // State for GIIN validation message
  const [giinValidationMessage, setGiinValidationMessage] = useState<string | null>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, type, value, checked } = e.target as HTMLInputElement;
    const newValue = type === "checkbox" ? checked : value;

    // Handle Taxpayer type dropdown change
    if (name === "Taxpayer type") {
      // When type changes, clear the TIN input so user can enter new value
      updateField(name, newValue);
      updateField("IMY-8_us_tin", "");

      // Clear validation message
      setTinValidationMessage("");

      return;
    }

    // Handle TIN validation and auto-detection for W-8IMY special types
    if (name === "IMY-8_us_tin") {
      const tinValue = newValue ? newValue.toString().trim() : "";
      const currentType = formData["Taxpayer type"];

      if (tinValue) {
        // Validate TIN format (handles EIN, SSN, ITIN, QIEIN, WPEIN, WTEIN, GIIN)
        const validation = validateTINFormat(tinValue, currentType);

        // Set validation message
        setTinValidationMessage(validation.message);

        // If valid and detected type exists, and NO type is selected, auto-select it
        if (
          validation.isValid &&
          validation.detectedType &&
          (!currentType || currentType === "" || currentType === "---Select---")
        ) {
          updateField("Taxpayer type", validation.detectedType);

          // If GIIN detected, check the GIIN checkbox
          if (validation.detectedType === "GIIN" && !model?.isMapDone && !formData?.isMapDone) {
            updateField("GIINCheckBox", true);
          } else if (validation.detectedType === "GIIN") {
            // console.log(
            //   "🛡️ BLOCKED W8IMY GIIN auto-check from handleChange TIN validation - form already mapped"
            // );
          }
        }
      } else {
        setTinValidationMessage(null);
      }
    }

    // Debug logging for new branch fields
    if (name === "IMY-branchCity_State_state" || name === "IMY-branchCity_State_postal") {
      // console.log(`🔧 W8IMY Branch field update: ${name} = "${newValue}"`);
    }

    // Update the model through the hook
    updateField(name, newValue);

    // Auto-check GIIN checkbox when GIIN field has a value and validate format
    if (name === "IMY-9A_giin" && newValue && newValue.toString().trim()) {
      // 🛡️ CRITICAL PROTECTION: Only auto-check if form is not already mapped
      if (!model?.isMapDone && !formData?.isMapDone) {
        updateField("GIINCheckBox", true);
      } else {
        console.log("🛡️ BLOCKED W8IMY GIIN auto-check from handleChange - form already mapped");
      }

      // Validate GIIN format
      const giinValue = newValue.toString().trim();
      const giinPattern = /^[A-Z0-9]{6}\.[A-Z0-9]{5}\.[A-Z0-9]{2}\.[A-Z0-9]{3}$/i;

      if (giinPattern.test(giinValue)) {
        setGiinValidationMessage("✓ Valid GIIN format");
      } else {
        setGiinValidationMessage(
          "⚠️ Invalid GIIN format. Expected: XXXXXX.XXXXX.XX.XXX (e.g., G018AA.99999.SL.777)"
        );
      }
    } else if (name === "IMY-9A_giin") {
      // Clear validation message when GIIN field is empty
      setGiinValidationMessage(null);
    }

    // Auto-check Foreign TIN checkbox when IMY-9B_foreign_tax_identifying_number has a value
    if (
      name === "IMY-9B_foreign_tax_identifying_number" &&
      newValue &&
      newValue.toString().trim()
    ) {
      // 🛡️ CRITICAL PROTECTION: Only auto-check if form is not already mapped
      if (!model?.isMapDone && !formData?.isMapDone) {
        updateField("Foreign TIN", true);
      } else {
        console.log(
          "🛡️ BLOCKED W8IMY Foreign TIN auto-check from handleChange - form already mapped"
        );
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any previous validation errors
    setShowValidationErrors(false);

    if (!validate()) {
      setShowValidationErrors(true);

      alert("Please fill in all required fields before submitting.");
      return;
    }

    try {
      // Use the model directly as the source of truth for current form values
      const currentFormValues: any = {};

      // Get all current form values from the model (which is always up-to-date)
      Object.keys(model).forEach((key) => {
        currentFormValues[key] = (model as any)[key];
      });

      // Set mapping done flag to prevent re-mapping
      currentFormValues.isMapDone = true;

      currentFormValues["Permanent city address"] = "";
      currentFormValues["Mailing address city"] = "";
      currentFormValues["Entity Type"] = "";
      currentFormValues["Chapter4Status"] = "";
      currentFormValues["EntityType"] = "";

      // Create a new model with the current form values
      const saveModel = new W8IMYFormModel(currentFormValues);

      // Save directly using the service with our collected current values
      const success = await W8IMYService.saveToApi(saveModel, row, selectedIndex);

      if (success) {
        // Clear validation errors on successful save
        setShowValidationErrors(false);

        if (onSaveSuccess) {
          onSaveSuccess(row);
        }
        alert("Form saved successfully");
      }
    } catch (error) {
      console.error("Failed to save W-8IMY form:", error);
      alert("Failed to save form data");
    }
  };

  return (
    <div className="scroll-container" style={headerStyle(isMenuOpen)}>
      <div>
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100% !important",
            minWidth: "100%",
            maxWidth: "100%",
            flex: "1 1 100%",
            display: "flex !important",
            flexDirection: "column",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {/* Validation Summary - Only show after user tries to submit */}
            {showValidationErrors && Object.keys(errors).length > 0 && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                  padding: "12px",
                  margin: "16px 0",
                  color: "#b91c1c",
                }}
              >
                <h4 style={{ margin: "0 0 8px 0", color: "#991b1b" }}>
                  Please fix the following errors:
                </h4>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {Object.entries(errors).map(([field, error], index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      <strong>{field.replace("IMY-", "").replace(/_/g, " ")}:</strong> {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Part I: Identification of Entity */}
            <section style={sectionStyle}>
              <FormInput
                label={createRequiredLabel("Unique Form Identifier")}
                id={"UniqueIdentifier"}
                name={"UniqueIdentifier"}
                value={(formData["UniqueIdentifier"] as string) || ""}
                onChange={handleChange}
              />
              {(formData["entityUniqueIdentifier"] as string)?.trim() && (
                <FormInput
                  label={"Entity Unique Identifier"}
                  id={"entityUniqueIdentifier"}
                  name={"entityUniqueIdentifier"}
                  value={(formData["entityUniqueIdentifier"] as string) || ""}
                  onChange={handleChange}
                />
              )}
              {showValidationErrors && errors["UniqueIdentifier"] && (
                <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                  {errors["UniqueIdentifier"]}
                </div>
              )}

              <FormTitle value={"Part I: Identification of Entity"} />
              <FormInput
                label={"Name of the organization"}
                id={"Name of organization"}
                name={"Name of organization"}
                value={(formData["Name of organization"] as string) || ""}
                onChange={handleChange}
              />

              <CountryDropdown
                label="Country of incorporation or organization:"
                name="Country_of_incorporation_organization"
                id="Country_of_incorporation_organization"
                value={
                  typeof formData["Country_of_incorporation_organization"] === "string"
                    ? formData["Country_of_incorporation_organization"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Country_of_incorporation_organization",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Other"}
                name={"Other"}
                value={(formData["Other"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Name of disregarded entity receiving the payment (if applicable):"}
                id={"IMY-Disregarded_entity_name"}
                name={"IMY-Disregarded_entity_name"}
                value={(formData["IMY-Disregarded_entity_name"] as string) || ""}
                onChange={handleChange}
              />

              <FormsubTitle value={"Chapter 3 Status (entity type) (Must check one box only):"} />
              <FormCheckBox
                label={"Non-qualified Intermediary"}
                name={"IMY-4_Non_qualified_intermediary"}
                checked={!!formData["IMY-4_Non_qualified_intermediary"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Non-withholding Foreign Grantor Trust"}
                name={"IMY-4_Non_withholding_foreign_grantor_trust"}
                checked={!!formData["IMY-4_Non_withholding_foreign_grantor_trust"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Non-withholding Foreign Partnership"}
                name={"IMY-4_Non_withholding_foreign_partnership"}
                checked={!!formData["IMY-4_Non_withholding_foreign_partnership"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Non-withholding Foreign Simple Trust"}
                name={"IMY-4_Non_withholding_foreign_simple_trust"}
                checked={!!formData["IMY-4_Non_withholding_foreign_simple_trust"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Qualified intermediary (including a QDD)"}
                name={"IMY-4_QI"}
                checked={!!formData["IMY-4_QI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Territory Financial Institution"}
                name={"IMY-4_Territory_financial_institution"}
                checked={!!formData["IMY-4_Territory_financial_institution"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"U.S. Branch"}
                name={"IMY-4_US_branch"}
                checked={!!formData["IMY-4_US_branch"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Withholding Foreign Partnership"}
                name={"IMY-4_Withholding_foreign_partnership"}
                checked={!!formData["IMY-4_Withholding_foreign_partnership"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Withholding Foreign Trust"}
                name={"IMY-4_Withholding_foreign_trust"}
                checked={!!formData["IMY-4_Withholding_foreign_trust"]}
                onChange={handleChange}
              />

              <FormsubTitle value={"Chapter 4 Status (FATCA Status):"} />
              <FormCheckBox
                label={"Active NFFE"}
                name={"IMY-5_active_NFFE"}
                checked={!!formData["IMY-5_active_NFFE"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Certain investment entities that do not maintain financial accounts"}
                name={"IMY-5_certain_investment_entities"}
                checked={!!formData["IMY-5_certain_investment_entities"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Certified deemed-compliant FFI with only low-value accounts"}
                name={"IMY-5_certified_deemed_low_value"}
                checked={!!formData["IMY-5_certified_deemed_low_value"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Certified deemed-compliant limited life debt investment entity"}
                name={"IMY-5_certified_deemed_debt_entity"}
                checked={!!formData["IMY-5_certified_deemed_debt_entity"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Certified deemed-compliant nonregistering local bank"}
                name={"IMY-5_certified_deemed_local_bank"}
                checked={!!formData["IMY-5_certified_deemed_local_bank"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Certified deemed-compliant sponsored, closely held investment vehicle"}
                name={"IMY-5_certified_deemed_investment_vehicle"}
                checked={!!formData["IMY-5_certified_deemed_investment_vehicle"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Direct reporting NFFE"}
                name={"IMY-5_direct_reporting_NFFE"}
                checked={!!formData["IMY-5_direct_reporting_NFFE"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Excepted nonfinancial entity in liquidation or bankruptcy"}
                name={"IMY-5_excepted_non_financial_liquidation"}
                checked={!!formData["IMY-5_excepted_non_financial_liquidation"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Excepted non-financial group entity"}
                name={"IMY-5_excepted_non_financial_group"}
                checked={!!formData["IMY-5_excepted_non_financial_group"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Excepted non-financial start-up company"}
                name={"IMY-5_excepted_non_financial_startup"}
                checked={!!formData["IMY-5_excepted_non_financial_startup"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Excepted territory NFFE"}
                name={"IMY-5_excepted_territory_NFFE"}
                checked={!!formData["IMY-5_excepted_territory_NFFE"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Exempt retirement plans"}
                name={"IMY-5_exempt_retirement_plans"}
                checked={!!formData["IMY-5_exempt_retirement_plans"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Foreign central bank of issue"}
                name={"IMY-5_foreign_central_bank"}
                checked={!!formData["IMY-5_foreign_central_bank"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Non-participating FFI"}
                name={"IMY-5_non_participating_FFI"}
                checked={!!formData["IMY-5_non_participating_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Nonreporting IGA FFI"}
                name={"IMY-5_non_reporting_IGA_FFI"}
                checked={!!formData["IMY-5_non_reporting_IGA_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Owner documented FFI"}
                name={"IMY-5_owner_documented_FFI"}
                checked={!!formData["IMY-5_owner_documented_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Participating FFI"}
                name={"IMY-5_participating_FFI"}
                checked={!!formData["IMY-5_participating_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Passive NFFE"}
                name={"IMY-5_passive_NFFE"}
                checked={!!formData["IMY-5_passive_NFFE"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Publicly traded NFFE or NFFE affiliate of a publicly traded corporation"}
                name={"IMY-5_publicly_traded_NFFE"}
                checked={!!formData["IMY-5_publicly_traded_NFFE"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Registered deemed-compliant FFI"}
                name={"IMY-5_registered_deemed_compliant_FFI"}
                checked={!!formData["IMY-5_registered_deemed_compliant_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Reporting Model 1 FFI"}
                name={"IMY-5_reporting_model1_FFI"}
                checked={!!formData["IMY-5_reporting_model1_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Reporting Model 2 FFI"}
                name={"IMY-5_reporting_model2_FFI"}
                checked={!!formData["IMY-5_reporting_model2_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Restricted distributor"}
                name={"IMY-5_restricted_distributor"}
                checked={!!formData["IMY-5_restricted_distributor"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Sponsored direct reporting NFFE"}
                name={"IMY-5_sponsored_direct_reporting_NFFE"}
                checked={!!formData["IMY-5_sponsored_direct_reporting_NFFE"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Sponsored FFI"}
                name={"IMY-5_sponsored_FFI"}
                checked={!!formData["IMY-5_sponsored_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Territory financial institution"}
                name={"IMY-5_territory_financial_institution"}
                checked={!!formData["IMY-5_territory_financial_institution"]}
                onChange={handleChange}
              />
            </section>

            {/* Permanent address section */}
            <section style={sectionStyle}>
              <FormTitle value={"Permanent address"} />

              <FormInput
                label={"Line 1"}
                id={"Permanent residence address"}
                name={"Permanent residence address"}
                value={(formData["Permanent residence address"] as string) || ""}
                onChange={handleChange}
              />

              <FormInput
                label={"Line 2:"}
                id={"Permanent address line 2"}
                name={"Permanent address line 2"}
                value={(formData["Permanent address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Permanent address line 3"}
                name={"Permanent address line 3"}
                value={(formData["Permanent address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Permanent address city"}
                name={"Permanent address city"}
                value={(formData["Permanent address city"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state USCA"}
                    name={"Permanent address state USCA"}
                    value={(formData["Permanent address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>Non-U.S.:</span> State / Province as it appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state NonUS"}
                    name={"Permanent address state NonUS"}
                    value={(formData["Permanent address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Permanent address zip"}
                name={"Permanent address zip"}
                value={(formData["Permanent address zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="Permanent address country"
                id="Permanent address country"
                value={
                  typeof formData["Permanent address country"] === "string"
                    ? formData["Permanent address country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Permanent address country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Permanent address other"}
                name={"Permanent address other"}
                value={(formData["Permanent address other"] as string) || ""}
                onChange={handleChange}
              />

              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is care-of-address: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="yes"
                      checked={formData["Permanent address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="no"
                      checked={formData["Permanent address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                  {showValidationErrors && errors["Permanent address is care of"] && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      {errors["Permanent address is care of"]}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is P.O. Box: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="yes"
                      checked={formData["Permanent address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="no"
                      checked={formData["Permanent address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                  {showValidationErrors && errors["Permanent address is po box"] && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      {errors["Permanent address is po box"]}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is rural route: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="yes"
                      checked={formData["Permanent address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="no"
                      checked={formData["Permanent address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                  {showValidationErrors && errors["Permanent address is rural route"] && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      {errors["Permanent address is rural route"]}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Mailing address section */}
            <section style={sectionStyle}>
              <FormTitle value={"Mailing address"} />

              <FormInput
                label={"Line 1:"}
                id={"Mailing address"}
                name={"Mailing address"}
                value={(formData["Mailing address"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"Mailing address line 2"}
                name={"Mailing address line 2"}
                value={(formData["Mailing address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Mailing address line 3"}
                name={"Mailing address line 3"}
                value={(formData["Mailing address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Mailing city address"}
                name={"Mailing city address"}
                value={(formData["Mailing city address"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Mailing address state USCA"}
                    name={"Mailing address state USCA"}
                    value={(formData["Mailing address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>Non U.S.:</span> State / Province as it appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Mailing address state NonUS"}
                    name={"Mailing address state NonUS"}
                    value={(formData["Mailing address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Mailing address zip"}
                name={"Mailing address zip"}
                value={(formData["Mailing address zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="Mailing address country"
                id="Mailing address country"
                value={
                  typeof formData["Mailing address country"] === "string"
                    ? formData["Mailing address country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Mailing address country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Mailing address other"}
                name={"Mailing address other"}
                value={(formData["Mailing address other"] as string) || ""}
                onChange={handleChange}
              />

              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label>Is care-of-address:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is care of"
                      value="yes"
                      checked={formData["Mailing address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is care of"
                      value="no"
                      checked={formData["Mailing address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Is P.O. Box:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is po box"
                      value="yes"
                      checked={formData["Mailing address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is po box"
                      value="no"
                      checked={formData["Mailing address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Is rural route:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is rural route"
                      value="yes"
                      checked={formData["Mailing address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is rural route"
                      value="no"
                      checked={formData["Mailing address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </section>

            {/* Taxpayer identification section */}
            <section style={sectionStyle}>
              <FormTitle value={"Taxpayer identification"} />
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="Taxpayer type" style={{ display: "block", marginBottom: 4 }}>
                  Type:
                </label>
                <select
                  id="Taxpayer type"
                  name="Taxpayer type"
                  value={
                    typeof formData["Taxpayer type"] === "string" ? formData["Taxpayer type"] : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Taxpayer type",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">---Select---</option>
                  <option value="EIN">EIN</option>
                  <option value="QIEIN">QIEIN</option>
                  <option value="WPEIN">WPEIN</option>
                  <option value="WTEIN">WTEIN</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <FormInput
                  label="TIN:"
                  id="IMY-8_us_tin"
                  name="IMY-8_us_tin"
                  value={(formData["IMY-8_us_tin"] as string) || ""}
                  onChange={handleChange}
                  placeholder={
                    formData["Taxpayer type"] === "EIN"
                      ? "XX-XXXXXXX"
                      : formData["Taxpayer type"] === "QIEIN"
                        ? "XX-XXXXXXX-QI"
                        : formData["Taxpayer type"] === "WPEIN"
                          ? "XX-XXXXXXX-WP"
                          : formData["Taxpayer type"] === "WTEIN"
                            ? "XX-XXXXXXX-WT"
                            : "Enter TIN"
                  }
                />
                {tinValidationMessage && (
                  <div
                    style={{
                      marginTop: 4,
                      padding: "6px 8px",
                      backgroundColor: tinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                      border: `1px solid ${tinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                      borderRadius: 4,
                      fontSize: "12px",
                      color: tinValidationMessage.includes("✓") ? "#155724" : "#856404",
                    }}
                  >
                    {tinValidationMessage}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 16 }}>
                <FormCheckBox
                  label={"GIIN"}
                  name={"GIINCheckBox"}
                  checked={!!formData["GIINCheckBox"]}
                  onChange={handleChange}
                />
              </div>
              <FormInput
                label="GIIN Entered:"
                id="IMY-9A_giin"
                name="IMY-9A_giin"
                value={(formData["IMY-9A_giin"] as string) || ""}
                onChange={handleChange}
              />

              {/* GIIN Validation Message */}
              {giinValidationMessage && (
                <div
                  style={{
                    marginTop: 8,
                    marginBottom: 16,
                    padding: "8px 12px",
                    borderRadius: 4,
                    fontSize: "14px",
                    backgroundColor: giinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                    border: `1px solid ${giinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                    display: "flex",
                    alignItems: "center",
                    color: giinValidationMessage.includes("✓") ? "#155724" : "#856404",
                  }}
                >
                  {giinValidationMessage}
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <FormCheckBox
                  label={"GIIN Applied For"}
                  name={"GIIN Applied For"}
                  checked={!!formData["GIIN Applied For"]}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <FormCheckBox
                  label={"Foreign TIN"}
                  name={"Foreign TIN"}
                  checked={!!formData["Foreign TIN"]}
                  onChange={handleChange}
                />
              </div>
              <FormInput
                label="Foreign taxpayer id:"
                id="IMY-9B_foreign_tax_identifying_number"
                name="IMY-9B_foreign_tax_identifying_number"
                value={(formData["IMY-9B_foreign_tax_identifying_number"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label="Reference numbers:"
                id="IMY-10_reference_number"
                name="IMY-10_reference_number"
                value={(formData["IMY-10_reference_number"] as string) || ""}
                onChange={handleChange}
              />
            </section>

            {/* Part II: Disregarded Entity or Branch Receiving Payment */}
            <section style={sectionStyle}>
              <FormTitle
                value={
                  "Part II Disregarded Entity or Branch Receiving Payment. (Complete only if a disregarded entity with a GIIN or a branch of an FFI in a country other than the FFI's country of residence. Do not complete Part II for QDD branches)"
                }
              />
              <div style={{ fontWeight: 500, marginBottom: 8 }}>
                11 Chapter 4 Status (FATCA Status) of disregarded entity or branch receiving
                payment.
              </div>
              <FormCheckBox
                label={"Branch treated as nonparticipating FFI"}
                name={"IMY-11_Branch_non_participating_FFI"}
                checked={!!formData["IMY-11_Branch_non_participating_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Participating FFI"}
                name={"IMY-11_Participating_FFI"}
                checked={!!formData["IMY-11_Participating_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Reporting Model 1 FFI"}
                name={"IMY-11_Reporting_model1_FFI"}
                checked={!!formData["IMY-11_Reporting_model1_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Reporting Model 2 FFI"}
                name={"IMY-11_Reporting_model2_FFI"}
                checked={!!formData["IMY-11_Reporting_model2_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"U.S. Branch"}
                name={"IMY-11_US_Branch"}
                checked={!!formData["IMY-11_US_Branch"]}
                onChange={handleChange}
              />
              <div style={{ fontWeight: 500, marginTop: 24, marginBottom: 8 }}>
                12 Disregarded entity or branch address
              </div>

              <FormInput
                label={"Line 1:"}
                id={"IMY-12_disregarded_entity_address_of_branch"}
                name={"IMY-12_disregarded_entity_address_of_branch"}
                value={(formData["IMY-12_disregarded_entity_address_of_branch"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"Disregarded entity line 2"}
                name={"Disregarded entity line 2"}
                value={(formData["Disregarded entity line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label="City / Town:"
                id="IMY-12_disregarded_entity_city_state_of_branch"
                name="IMY-12_disregarded_entity_city_state_of_branch"
                value={(formData["IMY-12_disregarded_entity_city_state_of_branch"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label="State / Province:"
                id="IMY-branchCity_State_state"
                name="IMY-branchCity_State_state"
                value={(formData["IMY-branchCity_State_state"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label="Zip / Postal Code:"
                id="IMY-branchCity_State_postal"
                name="IMY-branchCity_State_postal"
                value={(formData["IMY-branchCity_State_postal"] as string) || ""}
                onChange={handleChange}
              />

              <CountryDropdown
                label="Country:"
                name="IMY-12_disregarded_entity_country_of_branch"
                id="IMY-12_disregarded_entity_country_of_branch"
                value={
                  typeof formData["IMY-12_disregarded_entity_country_of_branch"] === "string"
                    ? formData["IMY-12_disregarded_entity_country_of_branch"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "IMY-12_disregarded_entity_country_of_branch",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"13 GIIN (if any):"}
                id={"IMY-13_disregarded_entity_giin"}
                name={"IMY-13_disregarded_entity_giin"}
                value={(formData["IMY-13_disregarded_entity_giin"] as string) || ""}
                onChange={handleChange}
              />
            </section>

            {/* Part III Qualified Intermediary */}
            <section style={sectionStyle}>
              <FormTitle value={"Part III Qualified Intermediary"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>14:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-14_Certification_Qualified_Intermediary"
                    checked={!!formData["IMY-14_Certification_Qualified_Intermediary"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>15a:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-15A_primary_withholding_responsibility"
                    checked={!!formData["IMY-15A_primary_withholding_responsibility"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>15b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-15B_publicly_traded_partnerships"
                    checked={!!formData["IMY-15B_publicly_traded_partnerships"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>15c:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-15C_withholding_as_nominee"
                    checked={!!formData["IMY-15C_withholding_as_nominee"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>15d:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-15D_qualified_securities_lender"
                    checked={!!formData["IMY-15D_qualified_securities_lender"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>15e:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-15E_withholding_and_form_1099_reporting"
                    checked={!!formData["IMY-15E_withholding_and_form_1099_reporting"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>15f:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-15F_form_1099_reporting_for_us"
                    checked={!!formData["IMY-15F_form_1099_reporting_for_us"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>15g:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-15G_non_assumption_of_form_1099_reporting"
                    checked={!!formData["IMY-15G_non_assumption_of_form_1099_reporting"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>15h:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-15H_chapter4_withholding_rate_pool"
                    checked={!!formData["IMY-15H_chapter4_withholding_rate_pool"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>15i:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-15I_intermediary_orflow_through_entities"
                    checked={!!formData["IMY-15I_intermediary_orflow_through_entities"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>16a:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-16A_certification_qdd_meets_requirements"
                    checked={!!formData["IMY-16A_certification_qdd_meets_requirements"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                16b Entity classification of QDD:
              </div>
              <div>
                <label style={{ fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    name="IMY-16B_entity_classification_corporation"
                    checked={!!formData["IMY-16B_entity_classification_corporation"]}
                    onChange={handleChange}
                  />{" "}
                  Corporation
                </label>
                <br />
                <label style={{ fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    name="IMY-16B_entity_classification_partnership"
                    checked={!!formData["IMY-16B_entity_classification_partnership"]}
                    onChange={handleChange}
                  />{" "}
                  Partnership
                </label>
                <br />
                <label style={{ fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    name="IMY-16B_entity_classification_disregarded_entity"
                    checked={!!formData["IMY-16B_entity_classification_disregarded_entity"]}
                    onChange={handleChange}
                  />{" "}
                  Disregarded Entity
                </label>
              </div>
            </section>

            {/* Part IV Non-qualified Intermediary */}
            <section style={sectionStyle}>
              <FormTitle value={"Part IV Non-qualified Intermediary"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>17a:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-17A_not_acting_as_qi"
                    checked={!!formData["IMY-17A_not_acting_as_qi"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>17b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-17B_transmit_withholding_certificates"
                    checked={!!formData["IMY-17B_transmit_withholding_certificates"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>17c:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-17C_nqi_withholding_rate_pool"
                    checked={!!formData["IMY-17C_nqi_withholding_rate_pool"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>17d:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-17D_qualified_securities_lender"
                    checked={!!formData["IMY-17D_qualified_securities_lender"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>17e:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-17E_alternative_withholding_statement"
                    checked={!!formData["IMY-17E_alternative_withholding_statement"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part V Territory Financial Institution */}
            <section style={sectionStyle}>
              <FormTitle value={"Part V Territory Financial Institution"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>18a:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-18A_tfi_certification"
                    checked={!!formData["IMY-18A_tfi_certification"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>18b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-18B_tfi_us_person_treatment"
                    checked={!!formData["IMY-18B_tfi_us_person_treatment"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>18c:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-18C_transmit_withholding_certificates"
                    checked={!!formData["IMY-18C_transmit_withholding_certificates"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>18d:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-18D_publicly_traded_partnerships"
                    checked={!!formData["IMY-18D_publicly_traded_partnerships"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>18e:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-18E_us_person_and_nominee"
                    checked={!!formData["IMY-18E_us_person_and_nominee"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>18f:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-18F_tfi_not_nominee"
                    checked={!!formData["IMY-18F_tfi_not_nominee"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part VI: Certain U.S. Branches */}
            <section style={sectionStyle}>
              <FormTitle value={"Part VI: Certain U.S. Branches"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>19a:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-19A_us_branch_certification"
                    checked={!!formData["IMY-19A_us_branch_certification"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>19b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-19B_us_branch_agreement"
                    checked={!!formData["IMY-19B_us_branch_agreement"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>19c:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-19C_transmit_certificates"
                    checked={!!formData["IMY-19C_transmit_certificates"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>19d:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-19D_us_branch_ptp"
                    checked={!!formData["IMY-19D_us_branch_ptp"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>19e:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-19E_us_branch_nominee"
                    checked={!!formData["IMY-19E_us_branch_nominee"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>19f:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-19F_us_branch_not_nominee"
                    checked={!!formData["IMY-19F_us_branch_not_nominee"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part VII Withholding Foreign Partnership (WP) or Withholding Foreign Trust (WT) */}
            <section style={sectionStyle}>
              <FormTitle
                value={
                  "Part VII Withholding Foreign Partnership (WP) or Withholding Foreign Trust (WT)"
                }
              />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>20:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-20_withholding_foreign_partnership"
                    checked={!!formData["IMY-20_withholding_foreign_partnership"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part VIII Non-withholding Foreign Partnership, Simple Trust, or Grantor Trust */}
            <section style={sectionStyle}>
              <FormTitle
                value={
                  "Part VIII Non-withholding Foreign Partnership, Simple Trust, or Grantor Trust"
                }
              />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>21a:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-21A_foreign_partnership_trust"
                    checked={!!formData["IMY-21A_foreign_partnership_trust"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>21b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-21B_lower_tier_partnership"
                    checked={!!formData["IMY-21B_lower_tier_partnership"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>21c:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-21C_transfer_of_interest"
                    checked={!!formData["IMY-21C_transfer_of_interest"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>21d:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-21D_statement_for_modified_amount"
                    checked={!!formData["IMY-21D_statement_for_modified_amount"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>21e:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-21E_behalf_of_grantors"
                    checked={!!formData["IMY-21E_behalf_of_grantors"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>21f:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-21F_alternative_withholding_statement"
                    checked={!!formData["IMY-21F_alternative_withholding_statement"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part IX Nonparticipating FFI with Exempt Beneficial Owners */}
            <section style={sectionStyle}>
              <FormTitle value={"Part IX Nonparticipating FFI with Exempt Beneficial Owners"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>22:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-22_exempt_beneficial_owners"
                    checked={!!formData["IMY-22_exempt_beneficial_owners"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part X Sponsored FFI */}
            <section style={sectionStyle}>
              <FormTitle value={"Part X Sponsored FFI"} />
              <FormInput
                label={"23a Sponsoring Entity:"}
                id={"IMY-23A_name_of_sponsoring_entity"}
                name={"IMY-23A_name_of_sponsoring_entity"}
                value={(formData["IMY-23A_name_of_sponsoring_entity"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>23b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-23B_sponsored_ffi_investment"
                    checked={!!formData["IMY-23B_sponsored_ffi_investment"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>23c:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-23C_controlled_foreign_corporation"
                    checked={!!formData["IMY-23C_controlled_foreign_corporation"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XI Owner documented FFI */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XI Owner documented FFI"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>24a:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-24A_owner_documented_ffi"
                    checked={!!formData["IMY-24A_owner_documented_ffi"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>24b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-24B_FFI_owner_reporting_statement"
                    checked={!!formData["IMY-24B_FFI_owner_reporting_statement"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>24c:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-24C_owner_documented_ffi_auditor"
                    checked={!!formData["IMY-24C_owner_documented_ffi_auditor"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XII Certified Deemed-Compliant Nonregistered Local Bank */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XII Certified Deemed-Compliant Nonregistered Local Bank"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>25:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-25_nonregistering local bank _certification"
                    checked={!!formData["IMY-25_nonregistering local bank _certification"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XIII Certified Deemed-Compliant FFI with Only Low-Value Accounts */}
            <section style={sectionStyle}>
              <FormTitle
                value={"Part XIII Certified Deemed-Compliant FFI with Only Low-Value Accounts"}
              />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>26:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-26_low_value_accounts"
                    checked={!!formData["IMY-26_low_value_accounts"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XIV Certified Deemed-Compliant Sponsored, Closely Held Investment Vehicle */}
            <section style={sectionStyle}>
              <FormTitle
                value={
                  "Part XIV Certified Deemed-Compliant Sponsored, Closely Held Investment Vehicle"
                }
              />
              <FormInput
                label={"27a Sponsoring Entity:"}
                id={"IMY-27A_sponsored_investment_vehicle_name"}
                name={"IMY-27A_sponsored_investment_vehicle_name"}
                value={(formData["IMY-27A_sponsored_investment_vehicle_name"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>27b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-27B_sponsored_investment_vehicle"
                    checked={!!formData["IMY-27B_sponsored_investment_vehicle"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XV Certified Deemed-Compliant Limited Life Debt Investment Entity */}
            <section style={sectionStyle}>
              <FormTitle
                value={"Part XV Certified Deemed-Compliant Limited Life Debt Investment Entity"}
              />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>28:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-28_limited_life_debt"
                    checked={!!formData["IMY-28_limited_life_debt"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XVI Certified Deemed-Compliant Investment Advisors and Investment Managers */}
            <section style={sectionStyle}>
              <FormTitle
                value={
                  "Part XVI Certified Deemed-Compliant Investment Advisors and Investment Managers"
                }
              />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>29:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-29_financial_institution_solely"
                    checked={!!formData["IMY-29_financial_institution_solely"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XVII Restricted Distributor */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XVII Restricted Distributor"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>30a:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-30A_restricted_distributor"
                    checked={!!formData["IMY-30A_restricted_distributor"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>30b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-30B_restricted_distributor_agreement"
                    checked={!!formData["IMY-30B_restricted_distributor_agreement"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>30c:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-30C_restricted_distributor_current"
                    checked={!!formData["IMY-30C_restricted_distributor_current"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XVIII Foreign Central Bank of Issue */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XVIII Foreign Central Bank of Issue"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>31:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-31_central_bank"
                    checked={!!formData["IMY-31_central_bank"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            <section style={sectionStyle}>
              <FormTitle value={"Part XIX Nonreporting IGA FFI"} />
              <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
                <span style={{ display: "inline-block", minWidth: 40 }}>32:</span>{" "}
                <input
                  type="checkbox"
                  name="IMY-32_nonreporting_iga_ffi"
                  checked={!!formData["IMY-32_nonreporting_iga_ffi"]}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>Country:</label>
                <CountryDropdown
                  label=""
                  name="IMY-32_iga_country"
                  id="IMY-32_iga_country"
                  value={
                    typeof formData["IMY-32_iga_country"] === "string"
                      ? formData["IMY-32_iga_country"]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "IMY-32_iga_country",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label>Other</label>
                <FormInput
                  label={""}
                  id={"IMY-32-other"}
                  name={"IMY-32-other"}
                  value={(formData["IMY-32-other"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <FormCheckBox
                  label="Model 1 IGA"
                  name="IMY-32_model1_iga"
                  checked={!!formData["IMY-32_model1_iga"]}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <FormCheckBox
                  label="Model 2 IGA"
                  name="IMY-32_model2_iga"
                  checked={!!formData["IMY-32_model2_iga"]}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label>Treated as:</label>
                <FormInput
                  label={""}
                  id={"IMY-32_treated_as"}
                  name={"IMY-32_treated_as"}
                  value={(formData["IMY-32_treated_as"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label>Name of Trustee of Sponsor:</label>
                <FormInput
                  label={""}
                  id={"IMY-32_trustee_name"}
                  name={"IMY-32_trustee_name"}
                  value={(formData["IMY-32_trustee_name"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label>Trustee Is:</label>
                <label style={{ marginRight: 8 }}>
                  <input
                    type="checkbox"
                    name="IMY-32_trustee_us"
                    checked={!!formData["IMY-32_trustee_us"]}
                    onChange={handleChange}
                  />{" "}
                  U.S.
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="IMY-32_trustee_foreign"
                    checked={!!formData["IMY-32_trustee_foreign"]}
                    onChange={handleChange}
                  />{" "}
                  Foreign
                </label>
              </div>
            </section>

            {/* Part XX Exempt Retirement Plans */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XX Exempt Retirement Plans"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>33a:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-33A_retirement_plan_treaty"
                    checked={!!formData["IMY-33A_retirement_plan_treaty"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>33b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-33B_retirement_plan_employer"
                    checked={!!formData["IMY-33B_retirement_plan_employer"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>33c:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-33C_retirement_plan_small"
                    checked={!!formData["IMY-33C_retirement_plan_small"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>33d:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-33D_retirement_plan_401a"
                    checked={!!formData["IMY-33D_retirement_plan_401a"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>33e:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-33E_retirement_plan_investment"
                    checked={!!formData["IMY-33E_retirement_plan_investment"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>33f:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-33F_retirement_plan_government"
                    checked={!!formData["IMY-33F_retirement_plan_government"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XXI Excepted Nonfinancial Group Entity */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XXI Excepted Nonfinancial Group Entity"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>34:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-34_nonfinancial_group_entity"
                    checked={!!formData["IMY-34_nonfinancial_group_entity"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XXII Excepted Nonfinancial Start-Up Company */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XXII Excepted Nonfinancial Start-Up Company"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>35:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-35_startup_company_certification"
                    checked={!!formData["IMY-35_startup_company_certification"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  <b>Formed on Date</b>
                </label>
                <FormDate
                  label={""}
                  name={"IMY-35_startup_formation_date"}
                  value={(formData["IMY-35_startup_formation_date"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Part XXIII Excepted Nonfinancial Entity in Liquidation or Bankruptcy */}
            <section style={sectionStyle}>
              <FormTitle
                value={"Part XXIII Excepted Nonfinancial Entity in Liquidation or Bankruptcy"}
              />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>36:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-36_Liquidation_or_bankruptcy"
                    checked={!!formData["IMY-36_Liquidation_or_bankruptcy"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  <b>Filed on Date</b>
                </label>
                <FormDate
                  label={""}
                  name={"IMY-36_liquidation_filing_date"}
                  value={(formData["IMY-36_liquidation_filing_date"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Part XXIV Publicly Traded NFFE or NFFE Affiliate of a Publicly Traded Corporation */}
            <section style={sectionStyle}>
              <FormTitle
                value={
                  "Part XXIV Publicly Traded NFFE or NFFE Affiliate of a Publicly Traded Corporation"
                }
              />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>37a:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-37A_publicly_traded_nffe"
                    checked={!!formData["IMY-37A_publicly_traded_nffe"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>Securities Market:</label>
                <FormInput
                  label={""}
                  id={"IMY-37A_securities_market"}
                  name={"IMY-37A_securities_market"}
                  value={(formData["IMY-37A_securities_market"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>37b:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-37B_nffe_affiliate"
                    checked={!!formData["IMY-37B_nffe_affiliate"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>Entity Name:</label>
                <FormInput
                  label={""}
                  id={"IMY-37B_affiliate_entity_name"}
                  name={"IMY-37B_affiliate_entity_name"}
                  value={(formData["IMY-37B_affiliate_entity_name"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>Securities Market:</label>
                <FormInput
                  label={""}
                  id={"IMY-37B_affiliate_securities_market"}
                  name={"IMY-37B_affiliate_securities_market"}
                  value={(formData["IMY-37B_affiliate_securities_market"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Part XXV Excepted Territory NFFE */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XXV Excepted Territory NFFE"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>38:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-38_territory_nffe"
                    checked={!!formData["IMY-38_territory_nffe"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XXVI Active NFFE */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XXVI Active NFFE"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>39:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-39_active_nffe"
                    checked={!!formData["IMY-39_active_nffe"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XXVII Passive NFFE */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XXVII Passive NFFE"} />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>40:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-40_passive_nffe"
                    checked={!!formData["IMY-40_passive_nffe"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Part XXVIII Sponsored Direct Reporting NFFE */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XXVIII Sponsored Direct Reporting NFFE"} />
              <FormInput
                label={"41 Sponsoring Entity:"}
                id={"IMY-41_sponsor_name"}
                name={"IMY-41_sponsor_name"}
                value={(formData["IMY-41_sponsor_name"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", minWidth: 40 }}>42:</span>{" "}
                  <input
                    type="checkbox"
                    name="IMY-42_sponsored_direct_reporting"
                    checked={!!formData["IMY-42_sponsored_direct_reporting"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </section>

            {/* Certification Section */}
            <section style={sectionStyle}>
              <FormTitle value={"Part XXIX Certification"} />
              <div style={{ marginBottom: 24 }}>
                <span className="form-label">
                  I agree that I will submit a new form within 30 days if any certification on this
                  form becomes incorrect.
                </span>
                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">
                    Is signed: <span style={{ color: "red" }}>*</span>
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="yes"
                      checked={formData.isSigned === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="no"
                      checked={formData.isSigned === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                {showValidationErrors && errors["isSigned"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4, marginBottom: 12 }}>
                    {errors["isSigned"]}
                  </div>
                )}

                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">
                    Has the form certification been modified:{" "}
                    <span style={{ color: "red" }}>*</span>
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="yes"
                      checked={formData.isModified === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="no"
                      checked={formData.isModified === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                {showValidationErrors && errors["isModified"] && (
                  <div style={{ color: "red", fontSize: 12, marginTop: 4, marginBottom: 12 }}>
                    {errors["isModified"]}
                  </div>
                )}

                <FormInput
                  label={"Signed Name:"}
                  id={"Print_name"}
                  value={formData["Print_name"]}
                  name={"Print_name"}
                  onChange={handleChange}
                />
                {/* <FormInput label={"Signature:"} id={"Signature"} value={formData["Signature"]} name={"Signature"} onChange={handleChange} /> */}

                <div style={{ marginBottom: "1rem" }}>
                  <FormDate
                    label="Date:"
                    name="Signature_date"
                    value={formData["Signature_date"] || ""}
                    onChange={handleChange}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 12,
                      marginTop: 12,
                    }}
                  >
                    <input
                      type="checkbox"
                      id="Signature_unreadable"
                      checked={formData.Signature_unreadable || false}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "Signature_unreadable",
                            type: "checkbox",
                            checked: e.target.checked,
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      style={{ marginRight: 8 }}
                    />
                    <label htmlFor="Signature_unreadable">Check if signature is unreadable</label>
                  </div>
                </div>
              </div>
            </section>

            <div
              style={{
                display: "flex",
                marginTop: 20,
                marginBottom: 40,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                maxWidth: "100%",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <button
                type="submit"
                style={{
                  padding: "6px 16px",
                  backgroundColor: "var(--btn-primary-bg, #000000)",
                  color: "var(--btn-primary-text, white)",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderRadius: "0.375rem",
                  boxShadow: "none",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                Save Form
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default W8IMYForm;
